import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../redux/hooks';
import { logOut } from '../redux/reducers/authSlice';

const LAST_ACTIVITY_KEY = 'last_activity';
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const THROTTLE_MS = 30 * 1000; // 30 seconds

const DOM_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'touchmove',
];

function getLastActivity(): number {
  const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
  return stored ? parseInt(stored, 10) : Date.now();
}

function setLastActivity(time: number): void {
  localStorage.setItem(LAST_ACTIVITY_KEY, time.toString());
}

export function updateLastActivity(): void {
  setLastActivity(Date.now());
}

export function useIdleTimeout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastThrottledRef = useRef(0);
  const isLoggingOutRef = useRef(false);

  const handleTimeout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    dispatch(logOut()).finally(() => {
      navigate('/login');
      isLoggingOutRef.current = false;
    });
  }, [dispatch, navigate]);

  const scheduleTimeout = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    const lastActivity = getLastActivity();
    const elapsed = Date.now() - lastActivity;
    const remaining = IDLE_TIMEOUT_MS - elapsed;

    if (remaining <= 0) {
      handleTimeout();
    } else {
      timerRef.current = setTimeout(handleTimeout, remaining);
    }
  }, [handleTimeout]);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottledRef.current < THROTTLE_MS) return;
    lastThrottledRef.current = now;
    setLastActivity(now);
    scheduleTimeout();
  }, [scheduleTimeout]);

  useEffect(() => {
    // Initialize last_activity if not set
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      setLastActivity(Date.now());
    }

    // Check if already timed out (e.g., page refresh after idle period)
    const lastActivity = getLastActivity();
    if (Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
      handleTimeout();
      return;
    }

    scheduleTimeout();

    // DOM event listeners
    for (const event of DOM_EVENTS) {
      document.addEventListener(event, recordActivity, { passive: true });
    }

    // Cross-tab sync via storage event
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        scheduleTimeout();
      }
      // Another tab logged out
      if (e.key === 'auth_token' && e.newValue === null) {
        handleTimeout();
      }
    };
    window.addEventListener('storage', handleStorage);

    // Background tab detection
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const last = getLastActivity();
        if (Date.now() - last > IDLE_TIMEOUT_MS) {
          handleTimeout();
        } else {
          scheduleTimeout();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      for (const event of DOM_EVENTS) {
        document.removeEventListener(event, recordActivity);
      }
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [recordActivity, scheduleTimeout, handleTimeout]);
}
